import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, Images, Trash2, Upload } from "lucide-react";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnDark, btnGhost, cardCls, inputCls } from "./shared";

const CSV_HEADER = "sku,name,category_slug,price,compare_at_price,stock,description,is_new,is_bestseller,is_trending,is_active,images";

export default function BulkTab({ products, categories }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const bulkRef = useRef(null);
  const csvRef = useRef(null);
  const photoRef = useRef(null);
  const load = useCallback(() => api.get("/admin/uploads").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const [csvFile, setCsvFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const bulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/uploads", fd);
      toast.success(`${data.count} images staged`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
    e.target.value = "";
  };

  const assign = async (id, productId) => {
    if (!productId) return;
    const fd = new FormData();
    fd.append("product_id", productId);
    try {
      await api.patch(`/admin/uploads/${id}`, fd);
      toast.success("Image attached to product");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeStaged = async (id) => {
    if (!window.confirm("Remove this staged image?")) return;
    await api.delete(`/admin/uploads/${id}`).then(load).catch((e) => toast.error(apiError(e)));
  };

  const downloadTemplate = () => {
    const csv = CSV_HEADER + "\n,Example Gold Watch,vintage-watches,449,,10,Beautiful gold bracelet watch,true,false,false,true,watch-1.jpg|watch-2.jpg\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "product-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildFormData = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    photoFiles.forEach((f) => fd.append("images", f));
    return fd;
  };

  const runPreview = async () => {
    if (!csvFile) { toast.error("Choose a CSV or Excel file first"); return; }
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/import/preview", buildFormData(csvFile));
      setPreview(data.rows.map((r) => ({ ...r })));
      if (!data.rows.length) toast.warning("No product rows found in the file");
    } catch (err) { toast.error(apiError(err)); }
    setPreviewLoading(false);
  };

  const editRow = (i, k, v) => setPreview((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v, ok: true, warnings: r.warnings.filter((w) => w.startsWith("photos")) } : r)));

  const runImport = async () => {
    if (!preview?.length) return;
    setImporting(true);
    setImportResult(null);
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const csv = CSV_HEADER + "\n" + preview.map((r) =>
      ["", r.name, r.category_slug, r.price, "", r.stock, r.description, "true", "false", "false", "true", (r.images || []).join("|")].map(esc).join(",")
    ).join("\n") + "\n";
    const file = new File([csv], "import.csv", { type: "text/csv" });
    try {
      const { data } = await api.post("/admin/import/products", buildFormData(file));
      setImportResult(data);
      if (data.errors?.length) toast.warning(`Imported ${data.created} product(s) — ${data.errors.length} row warning(s) below`);
      else toast.success(`Imported ${data.created} product(s)`);
      setPreview(null);
      setCsvFile(null);
      setPhotoFiles([]);
    } catch (err) { toast.error(apiError(err)); }
    setImporting(false);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/products.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "anaya-products.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-bulk-tab" className="space-y-6">
      <div className={cardCls} data-testid="bulk-import-panel">
        <p className="mb-1 font-display text-lg">Bulk catalogue import (spreadsheet + photos)</p>
        <p className="mb-4 text-xs font-light text-muted-foreground">
          1. Download the template and fill one row per product (CSV or Excel .xlsx).
          2. In the images column, list that product&#8217;s photo filenames in order, separated by | .
          3. Choose the file and all photo files, preview, fix anything highlighted, then confirm.
          Category must be an existing category slug (e.g. vintage-watches) — unknown categories are skipped, never created. Products whose name already exists are skipped to prevent duplicates.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="csv-template-btn" onClick={downloadTemplate} className={btnGhost}><Download size={14} /> Download template</button>
          <button data-testid="csv-pick-btn" onClick={() => csvRef.current?.click()} className={btnGhost}>
            <Upload size={14} /> {csvFile ? csvFile.name : "Choose CSV / Excel"}
          </button>
          <input ref={csvRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="csv-import-input"
            onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setPreview(null); e.target.value = ""; }} />
          <button data-testid="photos-pick-btn" onClick={() => photoRef.current?.click()} className={btnGhost}>
            <Images size={14} /> {photoFiles.length ? `${photoFiles.length} photos selected` : "Choose photos"}
          </button>
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="photos-import-input"
            onChange={(e) => { setPhotoFiles(Array.from(e.target.files || [])); setPreview(null); e.target.value = ""; }} />
          <button data-testid="preview-import-btn" onClick={runPreview} disabled={previewLoading || !csvFile} className={btnDark}>
            <Eye size={14} /> {previewLoading ? "Checking…" : "Preview import"}
          </button>
          <button data-testid="csv-export-btn" onClick={exportCsv} className={btnGhost}><Download size={14} /> Export CSV</button>
        </div>

        {preview && (
          <div className="mt-5" data-testid="import-preview">
            <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
              Preview — fix any row below, then confirm. Rows marked with warnings are skipped unless corrected.
            </p>
            <div className="space-y-2">
              {preview.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3 ${r.ok ? "border-green-200 bg-green-50/60" : "border-amber-300 bg-amber-50/60"}`} data-testid={`preview-row-${i}`}>
                  <div className="grid gap-2 sm:grid-cols-4">
                    <input className={inputCls} data-testid={`preview-name-${i}`} value={r.name} onChange={(e) => editRow(i, "name", e.target.value)} placeholder="Name" />
                    <input className={inputCls} data-testid={`preview-category-${i}`} value={r.category_slug} onChange={(e) => editRow(i, "category_slug", e.target.value)} placeholder="category-slug"
                      list="category-slugs" />
                    <input className={inputCls} data-testid={`preview-price-${i}`} value={r.price} onChange={(e) => editRow(i, "price", e.target.value)} placeholder="Price ₹" />
                    <input className={inputCls} data-testid={`preview-images-${i}`} value={(r.images || []).join("|")} onChange={(e) => editRow(i, "images", e.target.value.split("|").map((s) => s.trim()).filter(Boolean))} placeholder="photo1.jpg|photo2.jpg" />
                  </div>
                  {r.warnings.length > 0 && <p className="mt-1.5 text-[11px] text-amber-700" data-testid={`preview-warning-${i}`}>{r.warnings.join(" · ")}</p>}
                </div>
              ))}
            </div>
            <datalist id="category-slugs">{categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}</datalist>
            <div className="mt-4 flex flex-wrap gap-3">
              <button data-testid="run-import-btn" onClick={runImport} disabled={importing} className={btnDark}>
                <Upload size={14} /> {importing ? "Importing…" : `Confirm import (${preview.length} products)`}
              </button>
              <button data-testid="cancel-import-btn" onClick={() => setPreview(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        )}

        {importResult && (
          <div className="mt-4 rounded-xl bg-white/70 p-4 text-xs" data-testid="import-result">
            <p className="font-medium text-green-700" data-testid="import-result-count">{importResult.created} product(s) created</p>
            {importResult.errors?.map((e, i) => <p key={i} className="mt-1 text-amber-700" data-testid={`import-error-${i}`}>{e}</p>)}
          </div>
        )}
      </div>

      <div className={cardCls}>
        <p className="mb-1 font-display text-lg">Staged images</p>
        <p className="mb-4 text-xs font-light text-muted-foreground">Upload many photos at once, review them here, then attach each to an existing product.</p>
        <div className="mb-5 flex flex-wrap gap-3">
          <button data-testid="bulk-upload-btn" onClick={() => bulkRef.current?.click()} disabled={busy} className={btnDark}>
            <Upload size={14} /> {busy ? "Uploading…" : "Bulk upload images"}
          </button>
          <input ref={bulkRef} type="file" accept="image/*" multiple className="hidden" data-testid="bulk-upload-input" onChange={bulkUpload} />
        </div>
        {items.length === 0 ? (
          <p className="rounded-2xl bg-white/60 p-8 text-center text-sm font-light text-muted-foreground" data-testid="uploads-empty">No staged images yet.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((u) => (
              <div key={u.id} className="rounded-2xl bg-white/60 p-4" data-testid={`staged-upload-${u.id}`}>
                <img src={imgUrl(u.url)} alt={u.original_filename} className="h-40 w-full rounded-xl object-cover" />
                <p className="mt-2 truncate text-xs text-muted-foreground">{u.original_filename}</p>
                {u.status === "staged" ? (
                  <div className="mt-2 flex items-center gap-2">
                    <select data-testid={`assign-select-${u.id}`} defaultValue="" onChange={(e) => assign(u.id, e.target.value)}
                      className="w-full rounded-xl border border-input bg-white/80 px-3 py-2 text-xs outline-none">
                      <option value="" disabled>Attach to product…</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button data-testid={`staged-delete-${u.id}`} onClick={() => removeStaged(u.id)} aria-label="Remove" className="rounded-full p-2 hover:bg-secondary hover:text-destructive"><Trash2 size={13} /></button>
                  </div>
                ) : (
                  <p className="mt-2 text-[10px] uppercase tracking-widest text-green-700">Assigned</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
